import React, { useEffect, useMemo, useState } from 'react';
import { guildImg } from '../utils/guildImage';

/**
 * **점령전별 방어 그래프.**
 *
 * 총 방성률 하나로는 "요즘도 막히나" 를 알 수 없다 — 옛날에 잘 막던 덱이 지금은 다 뚫리는
 * 일이 그 숫자 안에 묻힌다(실측: 전체 24.8% 인 덱이 마지막 점령전만 보면 56% 였다).
 *
 * 막대 하나가 **점령전 한 판**이다. 날짜로 묶으면 안 된다 — 점령전은 이틀에 걸쳐 벌어져서
 * 한 판이 두 막대로 쪼개진다. 그래서 전투기록 그룹(`SiegeGroup`)의 기간으로 묶고, 이름도
 * 그룹 이름(= 전투 시작일)을 그대로 쓴다.
 *
 * **높이는 그 판에서 받은 공격 수** — 같은 높이로 그리면 한 번 뚫린 판과 열 번 뚫린 판이
 * 같아 보인다. 아래 파랑이 막은 몫, 위 빨강이 뚫린 몫이다.
 *
 * **막대를 누르면** 그 판을 누가 쳤는지가 아래에 펼쳐진다 — 상대 길드별로 몇 대 맞고 몇 번
 * 막았나. 한 판에도 상대가 둘이라(1:1:1 매치업) 총합만으로는 어느 길드에 뚫렸는지 모른다.
 *
 * 방덱 탭과 통계 탭이 같은 그림을 쓴다. 기록의 모양(`date`, `result`)이 같아서 — 둘 다
 * `search-counter` 의 방어 기록이다 — 한 벌로 둔다.
 */

export interface TrendRecord {
    /** `2026.09.10` 꼴 */
    date?: string;
    /** 싸운 때(ISO). 어느 점령전인지는 이 값으로 짚는다 */
    createdAt?: string;
    /** 방어한 길드원 기준. win = 막아냄 */
    result?: string;
    /** 친 길드 */
    oppGuildName?: string;
}

interface Siege {
    name: string;
    start: number;
    end: number;
}

/**
 * 점령전 기간표. **한 번 받아 모듈에 남긴다** — 화면마다, 덱마다 다시 받을 값이 아니다
 * (판이 새로 생기는 것은 일주일에 한두 번이다).
 */
let siegeCache: Siege[] | null = null;
let siegePending: Promise<Siege[]> | null = null;

async function loadSieges(): Promise<Siege[]> {
    if (siegeCache) return siegeCache;
    if (siegePending) return siegePending;
    siegePending = (async () => {
        try {
            const res = await fetch('/api/siege-battles/groups?logType=2');
            const data = await res.json();
            const rows: Siege[] = (data?.groups || [])
                .map((g: any) => {
                    const start = new Date(g.startDate).getTime();
                    // 끝 시각이 없는 옛 그룹은 이틀로 본다 — 점령전이 이틀에 걸친다
                    const end = g.endDate ? new Date(g.endDate).getTime() : start + 2 * 24 * 3600 * 1000;
                    return { name: g.groupName || '', start, end };
                })
                .filter((g: Siege) => Number.isFinite(g.start))
                .sort((a: Siege, b: Siege) => a.start - b.start);
            siegeCache = rows;
            return rows;
        } catch {
            return [];
        } finally {
            siegePending = null;
        }
    })();
    return siegePending;
}

export default function DefenseTrend({
    records,
    compact = false,
    days = 12
}: {
    records: TrendRecord[] | null | undefined;
    /** 좁은 화면(폰)에서 글자·높이를 줄인다 */
    compact?: boolean;
    /** 최근 몇 판까지 그릴지. 더 그리면 막대가 실이 된다 */
    days?: number;
}) {
    const [sieges, setSieges] = useState<Siege[] | null>(siegeCache);
    useEffect(() => {
        if (siegeCache) { setSieges(siegeCache); return; }
        let alive = true;
        loadSieges().then((rows) => { if (alive) setSieges(rows); });
        return () => { alive = false; };
    }, []);

    const chart = useMemo(() => {
        if (!records || records.length === 0) return null;

        /**
         * 이 기록이 **어느 점령전인가.** 기간표를 못 받았거나 어느 판에도 안 걸리면(옛 기록)
         * 그날 날짜를 그대로 한 칸으로 쓴다 — 그리지 않고 버리면 숫자가 목록과 안 맞는다.
         */
        const bucketOf = (r: TrendRecord): { key: string; label: string; at: number } | null => {
            const at = r.createdAt ? new Date(r.createdAt).getTime() : NaN;
            if (sieges && Number.isFinite(at)) {
                for (let i = 0; i < sieges.length; i += 1) {
                    const g = sieges[i];
                    if (at >= g.start && at <= g.end) return { key: `g:${g.start}`, label: g.name, at: g.start };
                }
            }
            const date = r.date || '';
            if (!date) return null;
            return { key: `d:${date}`, label: date.slice(5), at: Number.isFinite(at) ? at : new Date(date.replace(/\./g, '-')).getTime() };
        };

        const byKey = new Map<string, {
            key: string; label: string; at: number; win: number; lose: number;
            guilds: Map<string, { name: string; win: number; lose: number }>;
        }>();
        for (const r of records) {
            const b = bucketOf(r);
            if (!b) continue;
            const row = byKey.get(b.key)
                || { key: b.key, label: b.label, at: b.at, win: 0, lose: 0, guilds: new Map() };
            if (r.result === 'win') row.win += 1; else row.lose += 1;

            // 누가 쳤나 — 한 판에 상대가 둘이라 길드별로 따로 센다
            const gname = r.oppGuildName || '알 수 없음';
            const g = row.guilds.get(gname) || { name: gname, win: 0, lose: 0 };
            if (r.result === 'win') g.win += 1; else g.lose += 1;
            row.guilds.set(gname, g);

            byKey.set(b.key, row);
        }
        // Map 의 iterator 를 그대로 펼치면 CRA 빌드가 막는다 (tsconfig target 이 es5)
        const rows = Array.from(byKey.values())
            .sort((a, b) => a.at - b.at)
            .slice(-days)
            .map((d) => ({
                ...d,
                total: d.win + d.lose,
                rate: Math.round((d.win / (d.win + d.lose)) * 100),
                guildRows: Array.from(d.guilds.values())
                    .map((g) => ({ ...g, total: g.win + g.lose }))
                    .sort((a, b) => b.total - a.total)
            }));
        if (rows.length === 0) return null;
        return { rows, max: Math.max.apply(null, rows.map((d) => d.total)) };
    }, [records, days, sieges]);

    /** 펼쳐 본 판. 같은 막대를 다시 누르면 접는다 */
    const [openKey, setOpenKey] = useState<string | null>(null);
    const open = chart ? chart.rows.find((d) => d.key === openKey) : null;

    if (!chart) return null;

    const tall = compact ? 62 : 72;

    return (
        <div style={{ marginBottom: 10 }}>
        <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: compact ? 4 : 6,
            height: compact ? 84 : 96,
            padding: '0 2px'
        }}>
            {chart.rows.map((d) => {
                const h = Math.max(6, Math.round((d.total / chart.max) * tall));
                const winH = Math.round((d.win / d.total) * h);
                return (
                    <div
                        key={d.key}
                        title={`${d.label} 점령전 · ${d.total}전 ${d.win}성공 ${d.lose}실패 (방성률 ${d.rate}%) — 누르면 상대 길드별로 보여 줍니다`}
                        onClick={() => setOpenKey(openKey === d.key ? null : d.key)}
                        style={{
                            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                            minWidth: 0, cursor: 'pointer'
                        }}
                    >
                        <span style={{ fontSize: compact ? 8 : 9, fontWeight: 700, color: d.rate >= 50 ? '#64B5F6' : '#aaa' }}>
                            {d.rate}%
                        </span>
                        <div style={{
                            width: '100%', height: h, borderRadius: 4, overflow: 'hidden',
                            display: 'flex', flexDirection: 'column', background: '#e57373',
                            // 펼쳐 본 막대는 테두리로 짚어 준다 — 아래 표가 어느 판인지 알아야 한다
                            outline: openKey === d.key ? '2px solid #667eea' : 'none',
                            outlineOffset: 1,
                            opacity: openKey && openKey !== d.key ? 0.45 : 1
                        }}>
                            {/* 막은 만큼만 아래에서 파랗게 채운다 */}
                            <div style={{ marginTop: 'auto', height: winH, background: '#64B5F6' }} />
                        </div>
                        <span style={{
                            fontSize: compact ? 8 : 9, whiteSpace: 'nowrap',
                            color: openKey === d.key ? '#667eea' : '#aaa',
                            fontWeight: openKey === d.key ? 800 : 400,
                            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%'
                        }}>{d.label}</span>
                    </div>
                );
            })}
        </div>

        {/* 누른 판의 상대 길드별 전적 */}
        {open && (
            <div style={{
                marginTop: 8, padding: compact ? '8px 10px' : '10px 12px', borderRadius: 10,
                border: '1px solid rgba(102, 126, 234, 0.25)', background: 'rgba(102, 126, 234, 0.06)'
            }}>
                <div style={{
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                    fontSize: compact ? 11 : 12, fontWeight: 800, color: '#667eea', marginBottom: 6
                }}>
                    <span>{open.label} 점령전</span>
                    <span style={{ fontWeight: 700, color: '#888' }}>
                        {open.total}전
                        <span style={{ color: '#64B5F6', marginLeft: 6 }}>{open.win}성공</span>
                        <span style={{ color: '#e57373', marginLeft: 4 }}>{open.lose}실패</span>
                        <span style={{ marginLeft: 6 }}>방성률 {open.rate}%</span>
                    </span>
                    <button
                        onClick={() => setOpenKey(null)}
                        style={{
                            marginLeft: 'auto', border: 'none', background: 'none', color: '#999',
                            fontSize: compact ? 14 : 16, lineHeight: 1, cursor: 'pointer', padding: 0
                        }}
                        aria-label="접기"
                    >×</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {open.guildRows.map((g) => (
                        <div
                            key={g.name}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                fontSize: compact ? 11 : 12, color: '#555'
                            }}
                        >
                            <img loading="lazy"
                                src={guildImg(g.name)}
                                alt=""
                                style={{
                                    width: compact ? 16 : 18, height: compact ? 16 : 18, borderRadius: 5,
                                    objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(0, 0, 0, 0.08)'
                                }}
                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/images/unknown2.png'; }}
                            />
                            <span style={{
                                flex: 1, minWidth: 0, fontWeight: 700,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>{g.name}</span>
                            <span style={{ flexShrink: 0, color: '#888' }}>{g.total}전</span>
                            <span style={{ flexShrink: 0, width: compact ? 42 : 48, textAlign: 'right', color: '#64B5F6', fontWeight: 700 }}>
                                {g.win}성공
                            </span>
                            <span style={{ flexShrink: 0, width: compact ? 42 : 48, textAlign: 'right', color: '#e57373', fontWeight: 700 }}>
                                {g.lose}실패
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )}
        </div>
    );
}
